import { useQueryClient } from '@tanstack/react-query';
import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { authApi } from '../apis';
import { useLogin } from '../hooks/auth/use-auth';
import authService from '../lib/auth/auth-service';
import tokenStorage from '../lib/auth/token-storage';
import { SessionContext } from './use-session';

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const isLoggingOut = useRef(false);

  // Reactive isLoggedIn state
  const [isLoggedIn, setIsLoggedIn] = useState(() => authService.isLogged());

  // API Hooks
  const loginMutation = useLogin();

  // Loading state
  const isLoading = false;

  const login = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    try {
      const data = await loginMutation.mutateAsync({ username, password });
      if (data.accessToken) {
        authService.onLogin(data.accessToken, data.refreshToken);
        setIsLoggedIn(true);
        // Force refresh all queries to fetch new data
        await queryClient.invalidateQueries();
        return true;
      }
      return false;
    } catch (err) {
      console.warn('Login failed', err);
      return false;
    }
  };

  const logout = async () => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    try {
      const refreshToken = tokenStorage.getRefreshToken();
      if (refreshToken) {
        try {
          await authApi.logout(refreshToken);
        } catch (err) {
          console.warn('Server logout failed', err);
        }
      }
    } finally {
      authService.onLogout();
      setIsLoggedIn(false);
      isLoggingOut.current = false;
    }
  };

  // Declaratively clear cache only after unmounting has completed post-logout
  useEffect(() => {
    if (!isLoggedIn) {
      queryClient.clear();
    }
  }, [isLoggedIn, queryClient]);

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };
    window.addEventListener('unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
    };
  }, [logout]);

  return (
    <SessionContext.Provider
      value={{
        isLoggedIn,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
